// app/context/GraphContext.js
// import { changeLanguage } from 'i18next';
import { prepareGraphData } from '../utils/graph_utils';
import React, { createContext, useReducer, useMemo, useEffect, useCallback } from 'react';
import graph_api from '../utils/graph_api';
import graphSettings from '../config/graphSettings';

import {
    INIT,
    RELOAD_DATA,
    SET_DATA,
    SET_SETTINGS,
    SELECT_NODE,
    SELECT_ROOT,
    OPEN_EDIT_DIALOG,
    SAVE_NODE_CHANGES,
    OPEN_PROPOSE_DIALOG,
    OPEN_PROPOSAL_DIALOG,
    OPEN_EDIT_POSTS_DIALOG,
    VIEW_PROPOSAL,
    RESET_CHANGES,
    CHANGE_LANGUAGE,
    DELETE_NODE_CLOSE_DIALOG,
    CLOSE_DIALOG,
    OPEN_COMMIT_DIALOG,
    START_COMMIT_CHANGES,
    COMMIT_CHANGES_SUCCESS,
    COMMIT_CHANGES_FAILED,
    RESET_COMMIT_CHANGES,
    REJECT_PROPOSAL,
    SUBMIT_PROPOSAL,
    PROPOSAL_SUBMITTED_SUCCESS,
    PROPOSAL_SUBMITTED_FAILED,
    BUTTON_POST_ADDED,
    BUTTON_POST_UPDATED,
    BUTTON_POST_DELETED,
    BUTTON_POSTS_UPDATED,
    SHOW_MEDIA,
    LOAD_ERROR,
    TOGGLE_EDIT_MODE,
    MOVE_NODE,
    REORDER_NODE,
    ADD_CHILD_NODE,
    UNDO,
    REDO,
  } from './actionTypes';
import i18n from '../i18n';

let _nextTempId = 1;

// Check for admin mode from the URL
const searchParams = new URLSearchParams(window.location.search);
const isAdmin = searchParams.has('admin');

// Initial state
const initialState = {
    data: {},
    settings: {},
    language: 'gb',
    proposals: [],
    rootNodeId: null,
    selectedNode: null,
    selectedProposal: null,
    dialog: null,
    changeset: [],
    commitChanges: false,
    commitResult: null,
    commitError: null,
    isSubmittingProposal: false,
    proposalData: null,
    buttonPosts: [],
    proposalSubmitError: null,
    loadError: false,
    initialized: false,
    isAdmin: isAdmin,
    editMode: false,
    redoStack: [],
};

function graphReducer(state, action) {
    console.log(action);
    switch (action.type) {
        case INIT: {
            const language = localStorage.getItem('SelectedLanguage') || action.payload.settings.defaultLanguage;
            const rootNodeId = (localStorage.getItem('rootNodeId') || action.payload.settings.defaultRootNodeId || "4") + "";

            return {
                ...state,
                data: action.payload.data,
                proposals: action.payload.proposals,
                settings: action.payload.settings,
                language: language,
                rootNodeId: rootNodeId,
                buttonPosts: action.payload.buttonPosts,
                initialized: true,
                isAdmin: isAdmin,
            };
        }
        case RELOAD_DATA: {
            return {
                ...state,
                data: action.graph_data,
                proposals: action.proposals,
                changeset: [],
                selectedProposal: null,
                dialog: null,
                redoStack: [],
            };
        }
        case SET_DATA: {
            return {
                ...state,
                data: action.data,
            };
        }
        case SET_SETTINGS: {
            return {
                ...state,
                settings: action.settings,
            };
        }
        case SELECT_NODE: {
            return {
                ...state,
                selectedNode: action.node.id,
                dialog: 'view',
            };
        }
        case SELECT_ROOT: {
            let newRootNodeId = state.settings.defaultRootNodeId;
            const selectedNode = action.node;
            const data = state.data;

            if (action.node.id === state.rootNodeId && selectedNode.parentNode && selectedNode.parentNode.id) {
                newRootNodeId = selectedNode.parentNode.id;
            } else if (selectedNode.id) {
                newRootNodeId = action.node.id;
            }

            if (!data.nodes[newRootNodeId]) {
                newRootNodeId = state.settings.defaultRootNodeId;
            }

            localStorage.setItem('rootNodeId', newRootNodeId);
            const { graph, settings } = prepareGraphData(
                state.data,
                state.settings,
                state.language,
                null,
                newRootNodeId
            );

            return {
                ...state,
                graph,
                settings,
                rootNodeId: newRootNodeId,
                dialog: null,
            };
        }
        case OPEN_EDIT_DIALOG: {
            return {
                ...state,
                dialog: 'edit',
            };
        }
        case SAVE_NODE_CHANGES: {
            const change = action.change;
            // Generate a temp id for 'add' changes if not already set
            if (change.type === 'add' && !change.id) {
                change.id = `new-${change.parent}-${_nextTempId++}`;
            }
            return {
                ...state,
                changeset: [...state.changeset, change],
                redoStack: [],
            };
        }
        case OPEN_PROPOSE_DIALOG: {
            return {
                ...state,
                dialog: 'propose',
            };
        }
        case OPEN_PROPOSAL_DIALOG: {
            return {
                ...state,
                dialog: 'proposal',
                selectedProposal: action.proposalId,
            };
        }
        case VIEW_PROPOSAL: {
            return {
                ...state,
                changeset: [...action.changeset],
                selectedProposal: action.proposalId,
                dialog: null,
                redoStack: [],
            };
        }
        case RESET_CHANGES: {
            return {
                ...state,
                changeset: [],
                selectedProposal: null,
                redoStack: [],
            };
        }
        case CHANGE_LANGUAGE: {
            localStorage.setItem('SelectedLanguage', action.language);
            i18n.changeLanguage(action.language);
            // Re-prepare graph data with the new language
            const { graph, settings } = prepareGraphData(
                state.data,
                state.settings,
                action.language,
                null,
                state.rootNodeId
            );
            return {
                ...state,
                language: action.language,
                graph: graph,
                settings: settings,
            };
        }
        case DELETE_NODE_CLOSE_DIALOG: {
            return {
                ...state,
                changeset: [...state.changeset, action.change],
                dialog: null,
                redoStack: [],
            };
        }
        case CLOSE_DIALOG: {
            return {
                ...state,
                dialog: null,
            };
        }
        case OPEN_COMMIT_DIALOG: {
            return {
                ...state,
                dialog: 'commit',
            };
        }
        case START_COMMIT_CHANGES: {
            return {
                ...state,
                commitChanges: true,
            };
        }
        case COMMIT_CHANGES_SUCCESS: {
            return {
                ...state,
                commitChanges: false,
                commitResult: action.result,
                changeset: [], // Clear changeset after successful commit
                selectedProposal: null,
                dialog: null,
                redoStack: [],
                // Apply fresh data if provided, so changeset clear and data update happen atomically
                ...(action.data ? { data: action.data } : {}),
            };
        }
        case COMMIT_CHANGES_FAILED: {
            return {
                ...state,
                commitChanges: false,
                commitError: action.error,
                dialog: null,
            };
        }
        case RESET_COMMIT_CHANGES: {
            return {
                ...state,
                commitChanges: false,
                commitResult: null,
                commitError: null,
            };
        }
        case REJECT_PROPOSAL: {
            // Optionally, handle server-side rejection of the proposal
            return {
                ...state,
                proposals: state.proposals.filter(p => p.id !== action.proposalId),
                dialog: null,
            };
        }
        case SUBMIT_PROPOSAL: {
            return {
                ...state,
                isSubmittingProposal: true,
                proposalData: action.payload,
            };
        }
        case PROPOSAL_SUBMITTED_SUCCESS: {
            return {
                ...state,
                isSubmittingProposal: false,
                changeset: [],
                dialog: null,
                redoStack: [],
            };
        }
        case PROPOSAL_SUBMITTED_FAILED: {
            return {
                ...state,
                isSubmittingProposal: false,
                proposalSubmitError: action.error,
            };
        }
        case OPEN_EDIT_POSTS_DIALOG: {
            return {
                ...state,
                dialog: 'editPosts',
            };
        }
        case BUTTON_POST_ADDED: {
            return {
                ...state,
                buttonPosts: [...state.buttonPosts, action.payload],
            };
        }
        case BUTTON_POST_DELETED: {
            let buttonPosts = state.buttonPosts.filter(post => post.id !== action.payload.id);
            return {
                ...state,
                buttonPosts: buttonPosts,
            };
        }
        case BUTTON_POST_UPDATED: {
            let buttonPosts = state.buttonPosts.map(post => {
                if (post.id === action.payload.id) {
                    return action.payload;
                }
                return post;
            });
            return {
                ...state,
                buttonPosts: buttonPosts,
            };
        }
        case BUTTON_POSTS_UPDATED: {
            return {
                ...state,
                buttonPosts: action.payload,
            };
        }
        case SHOW_MEDIA: {
            return {
                ...state,
                dialog: 'media',
                dialogParams: action.media,
            };
        }
        case LOAD_ERROR: {
            return {
                ...state,
                loadError: true,
                initialized: true,
            };
        }
        case TOGGLE_EDIT_MODE: {
            const newEditMode = !state.editMode;
            localStorage.setItem('editMode', JSON.stringify(newEditMode));
            return {
                ...state,
                editMode: newEditMode,
            };
        }
        case MOVE_NODE: {
            return {
                ...state,
                changeset: [...state.changeset, action.change],
                redoStack: [],
            };
        }
        case REORDER_NODE: {
            return {
                ...state,
                changeset: [...state.changeset, action.change],
                redoStack: [],
            };
        }
        case ADD_CHILD_NODE: {
            const addChange = action.change;
            if (addChange.type === 'add' && !addChange.id) {
                addChange.id = `new-${addChange.parent}-${_nextTempId++}`;
            }
            return {
                ...state,
                changeset: [...state.changeset, addChange],
                redoStack: [],
            };
        }
        case UNDO: {
            if (state.changeset.length === 0) return state;
            const lastChange = state.changeset[state.changeset.length - 1];
            return {
                ...state,
                changeset: state.changeset.slice(0, -1),
                redoStack: [...state.redoStack, lastChange],
            };
        }
        case REDO: {
            if (state.redoStack.length === 0) return state;
            const change = state.redoStack[state.redoStack.length - 1];
            return {
                ...state,
                changeset: [...state.changeset, change],
                redoStack: state.redoStack.slice(0, -1),
            };
        }
        default: {
            console.warn('graphReducer, leaked action:', action);
            return state;
        }
    }
}

export const GraphContext = createContext();

// Provider component
export const GraphProvider = ({ children }) => {
    const [state, dispatch] = useReducer(graphReducer, initialState, (init) => {
        // On init, try to load changeset from localStorage
        const storedChanges = localStorage.getItem("changeset");
        if (storedChanges) {
            const parsed = JSON.parse(storedChanges);
            // Filter out corrupted 'add' changes that have no id
            init.changeset = parsed.filter(c => {
                if (c.type === 'add' && !c.id) {
                    console.warn('Removed corrupted add change (no id) from localStorage', c);
                    return false;
                }
                return true;
            });
        }
        // Restore redo stack from localStorage
        try {
            const storedRedo = localStorage.getItem("redoStack");
            if (storedRedo) init.redoStack = JSON.parse(storedRedo);
        } catch (_) {}
        // Restore edit mode
        try {
            const storedEditMode = localStorage.getItem("editMode");
            if (storedEditMode !== null) init.editMode = JSON.parse(storedEditMode);
        } catch (_) {}
        return init;
    });

    const { data, settings, language, changeset, rootNodeId } = state;

    useEffect(() => {
        // Whenever changeset or redo stack changes, store them
        localStorage.setItem("changeset", JSON.stringify(state.changeset));
        localStorage.setItem("redoStack", JSON.stringify(state.redoStack));
    }, [state.changeset, state.redoStack]);

    // Fetch initial data on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [graphData, buttonPosts] = await Promise.all([
                    graph_api.graph_data_request(),
                    graph_api.button_posts_request(),
                ]);
                let proposalsData = [];
                if (isAdmin) {
                    proposalsData = await graph_api.proposals_request();
                }
                dispatch({
                    type: INIT,
                    payload: {
                        data: graphData,
                        settings: graphSettings,
                        proposals: proposalsData,
                        buttonPosts: buttonPosts,
                    },
                });
            } catch (error) {
                console.error(error);
                dispatch({ type: LOAD_ERROR });
            }
        };
        fetchData();
    }, [dispatch]);

    // Memoize the graph computation
    const { graph, settings: preparedSettings } = useMemo(() => {
        return prepareGraphData(
            data,
            settings,
            language,
            changeset,
            rootNodeId
        );
    }, [data, settings, language, changeset, rootNodeId]);

    // Changeset size tracking
    const MAX_PROPOSAL_BYTES = 14 * 1024 * 1024; // 14MB client-side (1MB margin vs server 15MB cap)
    const WARN_PROPOSAL_BYTES = 10 * 1024 * 1024; // 10MB

    const changesetSize = useMemo(() => {
        if (!changeset || changeset.length === 0) return 0;
        return JSON.stringify(changeset).length;
    }, [changeset]);

    // Gate change-adding dispatches on size
    const dispatchChange = useCallback((action) => {
        const projected = JSON.stringify([...changeset, action.change]);
        if (projected.length > MAX_PROPOSAL_BYTES) {
            alert(i18n.t('proposalSizeLimitReached'));
            return false;
        }
        dispatch(action);
        return true;
    }, [changeset, dispatch]);

    // Provide the graph and updated settings via context
    const contextValue = {
        state,
        graph,
        settings: preparedSettings,
        dispatch,
        dispatchChange,
        changesetSize,
        MAX_PROPOSAL_BYTES,
        WARN_PROPOSAL_BYTES,
    };

    return (
        <GraphContext.Provider value={contextValue}>
            {children}
        </GraphContext.Provider>
    );
};
